import React, {useMemo} from "react";
import {useGetAllUsersQuery, useGetUsersOfRoleQuery} from "../../../../../../store/services/api/user-api";
import {Divider, Form, Input, Select, Tag,Collapse } from "antd";
import FilterContainer, {CoreFiltersBaseProps} from "../../../../components/FilterContainer";
import DateRangePicker from "../../../../../../components/forms/controls/DateRangePicker";
import {WorkOrderPriority, WorkOrderStatus, WorkOrderType} from "../../../../../../store/data/entities/work-order";
import {
  useGetAllBuildingsQuery,
  useGetAllFloorsQuery,
  useGetAllLocationsQuery
} from "../../../../../../store/services/api/building-api";
import {DateTime} from "luxon";
import { useGetAllAssetTypesQuery, useGetAllSubAssetTypesQuery } from "../../../../../../store/services/api/asset-type-api";
import { assetPriorityOptions } from "../../../../../../store/data/entities/asset";
import { useGetAllFallOutsQuery } from "../../../../../../store/services/api/fall-out-api";

type Building = {
  id: string;
  name: string;
};

type Floor = {
  id: string;
  name: string;
  buildingId: string;
};

type Location = {
  id: string;
  name: string;
  floorId: string;
};

type FilterOption = {
  label: string;
  value: string | number;
  disabled?: boolean;
};

const sortOptionsByLabel = <T extends FilterOption>(options: T[]) => {
  return [...options].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, {sensitivity: "base"})
  );
};

export default function WorkOrdersListScreenFilters({filter, onSubmit}: CoreFiltersBaseProps) {
  const { Panel } = Collapse;
  const {data: allUsers} = useGetAllUsersQuery();
  const {data: technicianUsers} = useGetUsersOfRoleQuery(['Technician', 'ExternalTechnician']);
  const buildingLeads = useMemo(() => {
    return allUsers?.filter(item => item.role === 'BuildingLead') ?? [];
  }, [allUsers]);
  const technicians = useMemo(() => {
    return technicianUsers ?? [];
  }, [technicianUsers]);
  const portalUsers = useMemo(() => {
    return allUsers?.filter(item => item.role !== 'Technician') ?? [];
  }, [allUsers]);

  const { data: fallOuts } = useGetAllFallOutsQuery(null); 
  const { data: buildings } = useGetAllBuildingsQuery(null);
  const { data: floors } = useGetAllFloorsQuery(null);
  const { data: locations } = useGetAllLocationsQuery(null);
  const { data: assetTypes } = useGetAllAssetTypesQuery(null);
  const { data: subAssetTypes } = useGetAllSubAssetTypesQuery(null);
  

  // Build maps for names
  const buildingMap: Map<string, string> = new Map(
    (buildings || []).map((b: Building) => [b.id, b.name])
  );
  const floorMap: Map<string, { name: string; buildingId: string }> = new Map(
    (floors || []).map((f: Floor) => [f.id, { name: f.name, buildingId: f.buildingId }])
  );
  // Get current filter values
  const selectedBuildingIds: string[] = filter.getFilter('buildingId') || [];
  const selectedFloorIds: string[] = filter.getFilter('floorId') || [];
  const selectedLocationIds: string[] = filter.getFilter('locationId') || [];
  const selectedAssetTypeIds: string[] = filter.getFilter("assetTypeId") || [];
  const selectedSubAssetTypeIds: string[] = filter.getFilter("subAssetTypeId") || [];

  /**
   * Handle Building changes
   */
  const handleOnBuildingChange = (buildingIds: string[]) => {
    filter.setFilter('buildingId', buildingIds);

    // prune floors
    const currentFloors: string[] = filter.getFilter('floorId') || [];
    const validFloors = (floors || [])
      .filter(f => buildingIds.includes(f.buildingId))
      .map(f => f.id);

    const prunedFloors = currentFloors.filter(floorId =>
      validFloors.includes(floorId)
    );
    filter.setFilter('floorId', prunedFloors);

    // prune locations 
    const currentLocations: string[] = filter.getFilter('locationId') || [];
    const validLocations = (locations || [])
      .filter(l => validFloors.includes(l.floorId))
      .map(l => l.id);

    const prunedLocations = currentLocations.filter(locId =>
      validLocations.includes(locId)
    );
    filter.setFilter('locationId', prunedLocations);
  };

  /**
   * Handle Floor changes
   */
  const handleOnFloorChange = (floorIds: string[]) => {
    filter.setFilter('floorId', floorIds);

    // prune locations
    const currentLocations: string[] = filter.getFilter('locationId') || [];
    const validLocations = (locations || [])
      .filter(l => floorIds.includes(l.floorId))
      .map(l => l.id);

    const prunedLocations = currentLocations.filter(locId =>
      validLocations.includes(locId)
    );
    filter.setFilter('locationId', prunedLocations);
  };

  /**
   * Floors filtered by buildings
   */
  const filteredFloors: Floor[] = !selectedBuildingIds.length
    ? (floors || [])
    : (floors || []).filter(f => selectedBuildingIds.includes(f.buildingId));

  const sortedFloors = filteredFloors
    .map(f => {
      const buildingName = buildingMap.get(f.buildingId) ?? '';
      return { ...f, label: `[${buildingName}] ${f.name}` };
    })
    .sort((a, b) => {
      const buildingA = buildingMap.get(a.buildingId) ?? '';
      const buildingB = buildingMap.get(b.buildingId) ?? '';
      return buildingA === buildingB
        ? a.name.localeCompare(b.name)
        : buildingA.localeCompare(buildingB);
    });

  /**
   * Locations filtered by floors
   */
  const filteredLocations: Location[] = !selectedFloorIds.length
    ? (locations || [])
    : (locations || []).filter(l => selectedFloorIds.includes(l.floorId));

  const sortedLocations = filteredLocations
    .map(l => {
      const floor = floorMap.get(l.floorId);
      const buildingName = floor ? buildingMap.get(floor.buildingId) ?? '' : '';
      const floorName = floor?.name ?? '';
      return {
        ...l,
        label: `[${buildingName}] [${floorName}] ${l.name}`,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  // Create a map for location labels
  const locationLabelMap: Map<string, string> = new Map(
    sortedLocations.map(l => [l.id, l.label])
  );

  const floorLabelMap: Map<string, string> = new Map(
      sortedFloors.map(l => [l.id, l.label])
    );

    const handleOnAssetTypeChange = (assetTypeIds: string[]) => {
    filter.setFilter("assetTypeId", assetTypeIds);

    // prune sub assets
    const currentSubAssets: string[] = filter.getFilter("subAssetTypeId") || [];
    const validSubAssets = (subAssetTypes || [])
      .filter((sa) => assetTypeIds.includes(sa.assetTypeId)) // assumes subAsset has `assetTypeId` reference
      .map((sa) => sa.id);

    const prunedSubAssets = currentSubAssets.filter((saId) =>
      validSubAssets.includes(saId)
    );
    filter.setFilter("subAssetTypeId", prunedSubAssets);
  };
  

  const filteredSubAssets = !selectedAssetTypeIds.length
    ? subAssetTypes || []
    : (subAssetTypes || []).filter((sa) =>
        selectedAssetTypeIds.includes(sa.assetTypeId)
      );

  // Create label with AssetType
  const subAssetLabelMap: Map<string, string> = new Map(
    filteredSubAssets.map((sa) => {
      const assetName =
        assetTypes?.find((a) => a.id === sa.assetTypeId)?.name ?? "";
      return [sa.id, `[${assetName}] ${sa.name}`];
    })
  );

  const sortedSubAssets = filteredSubAssets
    .map((sa) => ({
      ...sa,
      label: subAssetLabelMap.get(sa.id) || sa.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const sortedFallOutOptions = useMemo(() => {
    return sortOptionsByLabel(
      (fallOuts || []).map(item => ({
        label: item.fallOutCode,
        value: item.id
      }))
    );
  }, [fallOuts]);

  const sortedAssetTypeOptions = useMemo(() => {
    return sortOptionsByLabel(
      (assetTypes || []).map(item => ({
        label: item.name,
        value: item.id
      }))
    );
  }, [assetTypes]);

  const assetRankingOptions = useMemo(() => {
    return assetPriorityOptions.map(item => ({
      ...item,
      label: String(item.label)
    }));
  }, []);

  const sortedBuildingLeadOptions = useMemo(() => {
    return sortOptionsByLabel(
      buildingLeads.map(item => ({
        label: item.name,
        value: item.id
      }))
    );
  }, [buildingLeads]);

  const sortedTechnicianOptions = useMemo(() => {
    return sortOptionsByLabel(
      technicians.map(item => ({
        label: item.role === 'ExternalTechnician'
          ? `${item.name} (External)`
          : item.name,
        value: item.id
      }))
    );
  }, [technicians]);

  const sortedUserOptions = useMemo(() => {
    return sortOptionsByLabel(
      (allUsers || []).map(item => ({
        label: item.name,
        value: item.id
      }))
    );
  }, [allUsers]);

  const sortedBuildingOptions = useMemo(() => {
    return sortOptionsByLabel(
      (buildings || []).map(item => ({
        label: item.name,
        value: item.id,
      }))
    );
  }, [buildings]);

  const priorityOptions = useMemo(() => {
    return [
      {label: 'Standard', value: WorkOrderPriority.Standard},
      {label: 'Important', value: WorkOrderPriority.Important},
      {label: 'Urgent', value: WorkOrderPriority.Urgent},
      {label: 'Emergency', value: WorkOrderPriority.Emergency}
    ];
  }, []);

  const statusOptions = useMemo(() => {
    return [
      {label: 'Pending Approval', value: WorkOrderStatus.PendingApproval},
      {label: 'Approved', value: WorkOrderStatus.Approved},
      {label: 'Rejected', value: WorkOrderStatus.Rejected},
      {label: 'In Progress', value: WorkOrderStatus.InProgress},
      {label: 'Completed', value: WorkOrderStatus.Completed},
      {label: 'Closed', value: WorkOrderStatus.Closed},
      {label: 'Cancelled', value: WorkOrderStatus.Cancelled}
    ];
  }, []);

  const typeOptions = useMemo(() => {
    return [
      {label: 'Preventative', value: WorkOrderType.Preventative},
      {label: 'Corrective', value: WorkOrderType.Corrective},
      {label: 'Improvement', value: WorkOrderType.Improvement}
    ];
  }, []);

  
  // Custom tag render for locations with wrapped text
  const locationTagRender = (props: any) => {
    const { label, value, closable, onClose } = props;
    const locationLabel = locationLabelMap.get(value) || label;
    
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{ 
          margin: '2px', 
          padding: '2px 6px',
          maxWidth: '100%',
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          height: 'auto'
        }}
      >
        {locationLabel}
      </Tag>
    );
  };

  const floorTagRender = (props: any) => {
        const { label, value, closable, onClose } = props;
        const floorLabel = floorLabelMap.get(value) || label;
        
        return (
          <Tag
            closable={closable}
            onClose={onClose}
            style={{ 
              margin: '2px', 
              padding: '2px 6px',
              maxWidth: '100%',
              whiteSpace: 'normal',
              lineHeight: '1.4em',
              height: 'auto'
            }}
          >
            {floorLabel}
          </Tag>
        );
      };

    const subAssetTagRender = (props: any) => {
          const { label, value, closable, onClose } = props;
          const subAssetLabel = subAssetLabelMap.get(value) || label;
          
          return (
            <Tag
              closable={closable}
              onClose={onClose}
              style={{ 
                margin: '2px', 
                padding: '2px 6px',
                maxWidth: '100%',
                whiteSpace: 'normal',
                lineHeight: '1.4em',
                height: 'auto'
              }}
            >
              {subAssetLabel}
            </Tag>
          );
        };
      


  return (
    <FilterContainer
      onResetFilters={filter.resetFilters}
      onSubmit={onSubmit}
      isSubmitting={undefined}
    >
      <Collapse defaultActiveKey={['general','assignment','dates','priorityStatus','location']} ghost>
      <Panel header="General Info" key="general">
        <Form layout='vertical'>
          <Form.Item label='Reference'>
            <Input
              allowClear
              value={filter.getFilter('reference')}
              onChange={e => filter.setFilter('reference', e.target.value)}
            />
          </Form.Item>
          <Form.Item label='Parent WO Reference'>
            <Input
              allowClear
              value={filter.getFilter('parentReference')}
              onChange={e => filter.setFilter('parentReference', e.target.value)}
            />
          </Form.Item>
          <Form.Item label='Description'>
            <Input
              allowClear
              value={filter.getFilter('description')}
              onChange={e => filter.setFilter('description', e.target.value)}
            />
          </Form.Item>
          <Form.Item
          label="Fault Code"
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('fallOutId')}
            onChange={val => filter.setFilter('fallOutId', val)}
            options={sortedFallOutOptions}
          />
        </Form.Item>

        </Form>
      </Panel>

      <Panel header="Asset Info" key="asset">
        <Form layout='vertical'>
          <Form.Item label='Code'>
            <Input
              allowClear
              value={filter.getFilter('code')}
              onChange={e => filter.setFilter('code', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Asset Type">
            <Select
              mode="multiple"
              allowClear
              showSearch
              filterOption={(input, option) => {
                const label = String(option?.label ?? '');
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              value={selectedAssetTypeIds}
              onChange={handleOnAssetTypeChange}
              options={sortedAssetTypeOptions}
            />
          </Form.Item>



          <Form.Item label="Sub Asset Type">
            <Select
              mode="multiple"
              allowClear
              showSearch
              filterOption={(input, option) => {
                const text = String((option as any).searchText || '');
                return text.toLowerCase().includes(input.toLowerCase());
              }}
              value={selectedSubAssetTypeIds}
              onChange={(val) => filter.setFilter('subAssetTypeId', val)}
              tagRender={subAssetTagRender}
              dropdownRender={menu => (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {menu}
                </div>
              )}
              optionLabelProp="label"
              options={sortedSubAssets.map(item => ({
                label: (
                  <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                    {item.label}
                  </div>
                ),
                value: item.id,
                searchText: item.label,
              }))}
            />
          </Form.Item>

          <Form.Item label='Asset Ranking'>
            <Select
              mode="multiple"
              allowClear
              showSearch
              filterOption={(input, option) => {
                const text = String((option as any).label  || '');
                return text.toLowerCase().includes(input.toLowerCase());
              }}
              options={assetRankingOptions}
              value={filter.getFilter('assetPriority')}
              onChange={val => filter.setFilter('assetPriority', val)}
            >
            </Select>
          </Form.Item>

        </Form>
      </Panel>

      
      <Panel header="Assignment" key="assignment">
        <Form layout='vertical'>
          <Form.Item label='Owner'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('buildingLeadId')}
            onChange={val => filter.setFilter('buildingLeadId', val)}
            options={sortedBuildingLeadOptions}
          />
        </Form.Item>

        <Form.Item label='Technician'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('technicianId')}
            onChange={val => filter.setFilter('technicianId', val)}
            options={sortedTechnicianOptions}
          />
        </Form.Item>
        <Form.Item label='Created By'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('createdByUserId')}
            onChange={val => filter.setFilter('createdByUserId', val)}
            options={sortedUserOptions}
          />
        </Form.Item>
        <Form.Item label='Closed By'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('closedByUserId')}
            onChange={val => filter.setFilter('closedByUserId', val)}
            options={sortedUserOptions}
          />
        </Form.Item>

        </Form>
      </Panel>

      <Panel header="Dates" key="dates">
        <Form layout='vertical'>
          <Form.Item label='Scheduled Date'>
          <DateRangePicker
            placement="topLeft"
            rangeSelectionBehavior='future'
            onStartDateChange={(value) => {
              filter.setFilter('startDate', value?.toISO() ?? undefined);
            }}
            onEndDateChange={(value) => {
              filter.setFilter('endDate', value?.toISO() ?? undefined);
            }}
            startDateValue={filter.getFilter('startDate') ? DateTime.fromISO(filter.getFilter('startDate')) : undefined}
            endDateValue={filter.getFilter('endDate') ? DateTime.fromISO(filter.getFilter('endDate')) : undefined}
          />
        </Form.Item>
        <Form.Item label='Must Finish By'>
          <DateRangePicker
            placement="topLeft"
            rangeSelectionBehavior='future'
            onStartDateChange={(value) => {
              filter.setFilter('scheduledFinishStartDate', value?.toISO() ?? undefined);
            }}
            onEndDateChange={(value) => {
              filter.setFilter('scheduledFinishEndDate', value?.toISO() ?? undefined);
            }}
            startDateValue={filter.getFilter('scheduledFinishStartDate') ? DateTime.fromISO(filter.getFilter('scheduledFinishStartDate')) : undefined}
            endDateValue={filter.getFilter('scheduledFinishEndDate') ? DateTime.fromISO(filter.getFilter('scheduledFinishEndDate')) : undefined}
          />
        </Form.Item>
        <Form.Item label='Created On'>
          <DateRangePicker
            placement="topLeft"
            onStartDateChange={(value) => {
              filter.setFilter('createdOnStartDate', value?.toISO() ?? undefined);
            }}
            onEndDateChange={(value) => {
              filter.setFilter('createdOnEndDate', value?.toISO() ?? undefined);
            }}
            startDateValue={filter.getFilter('createdOnStartDate') ? DateTime.fromISO(filter.getFilter('createdOnStartDate')) : undefined}
            endDateValue={filter.getFilter('createdOnEndDate') ? DateTime.fromISO(filter.getFilter('createdOnEndDate')) : undefined}
          />
        </Form.Item>
        <Form.Item label='Closed On'>
          <DateRangePicker
            placement="topLeft"
            onStartDateChange={(value) => {
              filter.setFilter('closedOnStartDate', value?.toISO() ?? undefined);
            }}
            onEndDateChange={(value) => {
              filter.setFilter('closedOnEndDate', value?.toISO() ?? undefined);
            }}
            startDateValue={filter.getFilter('closedOnStartDate') ? DateTime.fromISO(filter.getFilter('closedOnStartDate')) : undefined}
            endDateValue={filter.getFilter('closedOnEndDate') ? DateTime.fromISO(filter.getFilter('closedOnEndDate')) : undefined}
          />
        </Form.Item>

        <Form.Item label='Labour Entries'>
          <DateRangePicker
          placement="topLeft"
          format="dd/MM/yyyy"
            onStartDateChange={(value) => {
              filter.setFilter('labourEntryStartDate', value?.toISODate() ?? undefined);
            }}
            onEndDateChange={(value) => {
              filter.setFilter('labourEntryEndDate', value?.toISODate() ?? undefined);
            }}
            startDateValue={filter.getFilter('labourEntryStartDate') ? DateTime.fromISO(filter.getFilter('labourEntryStartDate')) : undefined}
            endDateValue={filter.getFilter('labourEntryEndDate') ? DateTime.fromISO(filter.getFilter('labourEntryEndDate')) : undefined}
          />
        </Form.Item>

        </Form>
      </Panel>

      <Panel header="Status" key="priorityStatus">
        <Form layout='vertical'>
          <Form.Item label='Priority'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('priority')}
            onChange={val => filter.setFilter('priority', val)}
            options={priorityOptions}
          />
        </Form.Item>
        <Form.Item label='Status'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('statuses')}
            onChange={val => filter.setFilter('statuses', val)}
            options={statusOptions}
          />
        </Form.Item>

        <Form.Item label='Type'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            value={filter.getFilter('type')}
            onChange={val => filter.setFilter('type', val)}
            options={typeOptions}
          />
        </Form.Item>
        

        </Form>
      </Panel>

      <Panel header="Location" key="location">
        <Form layout='vertical'>
          <Form.Item label="Building">
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const label = String(option?.label ?? '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
            value={selectedBuildingIds}
            onChange={handleOnBuildingChange}
            options={sortedBuildingOptions}
          />
        </Form.Item>

        <Form.Item label="Floor">
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const label = String(option?.searchText ?? '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
            value={selectedFloorIds}
            onChange={handleOnFloorChange}
            tagRender={floorTagRender}
            dropdownRender={menu => (
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {menu}
              </div>
            )}
            optionLabelProp="label"
            options={sortedFloors.map(item => ({
              label: (
                <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                  {item.label}
                </div>
              ),
              value: item.id,
              searchText: item.label,
            }))}
          />
        </Form.Item>

        <Form.Item label="Location">
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const label = String(option?.searchText ?? '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
            value={selectedLocationIds}
            onChange={(val) => filter.setFilter('locationId', val)}
            tagRender={locationTagRender}
            dropdownRender={menu => (
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {menu}
              </div>
            )}
            optionLabelProp="label"
            options={sortedLocations.map(item => ({
              label: (
                <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                  {item.label}
                </div>
              ),
              value: item.id,
              searchText: item.label,
            }))}
          />
        </Form.Item>

        </Form>
      </Panel>

      </Collapse>
    </FilterContainer>
  )
}
